// ═══════════════════════════════════════════════════════════════════════════════
// Helper — after saving receipt(s): remind to submit settlement (bottom sheet)
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BudgetListItem } from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { budgetApi } from '../../lib/api';
import { portalToBody } from '../../lib/portalToBody';
import { MODAL_BACKDROP_SCRIM_CLASS } from '../../lib/modalClasses';
import { AppButton } from '../ui/AppButton';
import {
  buildSettlementsHrefForBudgets,
  matchMainBudgetsForReceiptDates,
} from '../../utils/matchBudgetsForReceiptDates';

type HelperSettlementNextStepSheetProps = {
  open: boolean;
  receiptDates: readonly string[];
  onClose: () => void;
};

export const HelperSettlementNextStepSheet = ({
  open,
  receiptDates,
  onClose,
}: HelperSettlementNextStepSheetProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [matchedBudgets, setMatchedBudgets] = useState<BudgetListItem[]>([]);
  const [loadingPeriods, setLoadingPeriods] = useState(false);

  const receiptDatesKey = receiptDates.join('|');

  useEffect(() => {
    if (!open) {
      return;
    }

    const dates = receiptDatesKey.split('|').filter(Boolean);
    let cancelled = false;
    const loadPeriods = async (): Promise<void> => {
      setLoadingPeriods(true);
      try {
        const res = await budgetApi.list('all', 1, 50, '2000-01-01', '9999-12-31');
        if (cancelled) {
          return;
        }
        if (!res.success || !res.data) {
          setMatchedBudgets([]);
          return;
        }
        const rows = Array.isArray(res.data) ? res.data : (res.data.items ?? []);
        setMatchedBudgets(matchMainBudgetsForReceiptDates(rows, dates));
      } catch {
        if (!cancelled) {
          setMatchedBudgets([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingPeriods(false);
        }
      }
    };

    void loadPeriods();
    return () => {
      cancelled = true;
    };
  }, [open, receiptDatesKey]);

  const handleClose = useCallback((): void => {
    onClose();
  }, [onClose]);

  const handleBackdropKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleClose();
      }
    },
    [handleClose],
  );

  const handleGoSettle = useCallback((): void => {
    const href = buildSettlementsHrefForBudgets(matchedBudgets);
    onClose();
    navigate(href);
  }, [matchedBudgets, navigate, onClose]);

  if (!open) {
    return null;
  }

  const settleableBudgets = matchedBudgets.filter((row) => !row.isLocked);
  const lockedOnly =
    matchedBudgets.length > 0 && settleableBudgets.length === 0;

  return portalToBody(
    <div className="fixed inset-0 z-[70] flex items-end justify-center" role="presentation">
      <div
        className={`absolute inset-0 ${MODAL_BACKDROP_SCRIM_CLASS}`}
        role="button"
        tabIndex={0}
        aria-label={t('common.close')}
        onClick={handleClose}
        onKeyDown={handleBackdropKeyDown}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="helper-settlement-next-step-title"
        className="relative z-10 w-full max-w-lg rounded-t-2xl border border-gray-200 bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom,0px))] shadow-xl dark:border-gray-700 dark:bg-gray-900"
      >
        <h2
          id="helper-settlement-next-step-title"
          className="text-lg font-bold text-gray-900 dark:text-gray-100"
        >
          {t('receipt.analyze.nextStepSheetTitle')}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-gray-700 dark:text-gray-200">
          {t('receipt.analyze.nextStepSheetLead')}
        </p>
        <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-relaxed text-gray-700 dark:text-gray-200">
          <li>{t('receipt.analyze.nextStepSheetUploadAll')}</li>
          <li>{t('receipt.analyze.nextStepSheetLockNote')}</li>
          <li>{t('receipt.analyze.nextStepSheetEmployerReview')}</li>
        </ul>

        <div className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50/90 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/30">
          {loadingPeriods ? (
            <p className="text-sm text-emerald-900 dark:text-emerald-100/90">
              {t('receipt.analyze.nextStepSheetPeriodLoading')}
            </p>
          ) : matchedBudgets.length === 0 ? (
            <p className="text-sm text-emerald-900 dark:text-emerald-100/90">
              {t('receipt.analyze.nextStepSheetPeriodUnknown')}
            </p>
          ) : (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-200/90">
                {t('receipt.analyze.nextStepSheetPeriodLabel')}
              </p>
              {matchedBudgets.map((budget) => (
                <p
                  key={budget.id}
                  className="text-sm font-medium tabular-nums text-emerald-950 dark:text-emerald-50"
                >
                  {t('receipt.analyze.nextStepSheetPeriodRange', {
                    start: budget.startDate,
                    end: budget.endDate,
                  })}
                  {budget.isLocked
                    ? ` — ${t('receipt.analyze.nextStepSheetPeriodLocked')}`
                    : ''}
                </p>
              ))}
            </div>
          )}
        </div>

        {lockedOnly ? (
          <p className="mt-3 text-sm text-amber-800 dark:text-amber-200" role="status">
            {t('receipt.analyze.nextStepSheetLockedHint')}
          </p>
        ) : null}

        <div className="mt-5 flex flex-col gap-2">
          {!lockedOnly ? (
            <AppButton
              variant="primary"
              size="lg"
              fullWidth
              onClick={handleGoSettle}
              aria-label={t('receipt.analyze.nextStepSheetGoSettleAria')}
              className="shadow-md"
            >
              {t('receipt.analyze.nextStepSheetGoSettle')}
            </AppButton>
          ) : null}
          <AppButton
            variant="secondary"
            fullWidth
            onClick={handleClose}
            aria-label={t('receipt.analyze.nextStepSheetLaterAria')}
            className="rounded-xl"
          >
            {t('receipt.analyze.nextStepSheetLater')}
          </AppButton>
        </div>
      </div>
    </div>,
  );
};
